import React, { useCallback, useEffect, useState } from 'react';
import { BEM } from '@feedfm/feed-util';
import { Form, OverlayTrigger, Popover } from 'react-bootstrap';

import './CountryListIcon.scss';

const bem = new BEM( 'fmg-country-list-icon' );

export interface CountryListIconProps {
  /** A list of explicitly included countries. */
  includedCountryCodes: string[];
  className?: string;
}

/** Displays an icon that can be expanded into a popover for showing a large list of countries. */
export const CountryListIcon: React.FC<CountryListIconProps> = ( { includedCountryCodes, className } ) => {
  const [filters, setFilters] = useState<null | string[]>( null );
  const [filteredCountries, setFilteredCountries] = useState<string[]>( includedCountryCodes );

  const [popoverId] = useState( () => {
    const randomId = Math.random().toString( 36 ).substr( 2 );
    return `country-list-display-popover-${randomId}`;
  } );

  const handleFilterChange = useCallback( ( event: React.ChangeEvent<HTMLInputElement> ) => {
    const newFilters = event.target.value.split( ',' ).map( ( countryFilter ) => countryFilter.trim().toUpperCase() );
    setFilters( newFilters );
  }, [] );

  useEffect( () => {
    if ( includedCountryCodes && filters ) {
      setFilteredCountries(
        includedCountryCodes.filter( ( country ) => filters.some( ( filter ) => country.includes( filter ) ) ),
      );
    } else {
      setFilteredCountries( includedCountryCodes );
    }
  }, [includedCountryCodes, filters] );

  const fullListPopover = (
    <Popover id={ popoverId } className={ BEM.$( bem.el( 'popover' ) ) }>
      <Popover.Title>
        { includedCountryCodes.length } included countries
        { includedCountryCodes.length > 20 && (
          <Form.Control size="sm" type="text" placeholder="Filter" onChange={ handleFilterChange } />
        ) }
      </Popover.Title>
      <Popover.Content>
        { filters && filters.length && (
          <div className={ BEM.$( bem.el( 'active-filters' ) ) }>Filtering to { filters.join( ', ' ) }</div>
        ) }
        { filteredCountries.join( ', ' ) || 'None' }
      </Popover.Content>
    </Popover>
  );

  return (
    <span className={ BEM.$( bem, className ) }>
      <OverlayTrigger trigger={ 'click' } overlay={ fullListPopover } rootClose>
        <span>🌍</span>
      </OverlayTrigger>
    </span>
  );
};
